import {
  AlertTriangle,
  BarChart3,
  Check,
  ChevronLeft,
  CircleDollarSign,
  Clock3,
  Coins,
  Database,
  LoaderCircle,
  LockKeyhole,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  UserRoundCheck,
  UsersRound,
  WalletCards,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  grantAdminCredits,
  loadAdminOverview,
  loadAdminUsers,
} from "../lib/api";
import type { AdminOverview, AdminUser, PublicConfig } from "../types";
import { Brand } from "./Brand";

type Props = {
  config: PublicConfig;
  onProfile: () => void;
};

const formatDate = (value: string | null) =>
  value
    ? new Intl.DateTimeFormat("it-IT", {
        day: "2-digit",
        month: "short",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(value))
    : "Mai";

const formatMoney = (cents: number) =>
  new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
  }).format(cents / 100);

const readinessItems = (overview: AdminOverview) => [
  {
    label: "Generazione OpenAI",
    detail: overview.readiness.generation ? "Operativa" : "Non configurata",
    ok: overview.readiness.generation,
  },
  {
    label: "Pagamenti Stripe",
    detail: overview.readiness.payments ? "Operativi" : "Da configurare",
    ok: overview.readiness.payments,
  },
  {
    label: "Dati legali",
    detail: overview.readiness.legal ? "Completi" : "Incompleti",
    ok: overview.readiness.legal,
  },
  {
    label: "Accesso amministratori",
    detail: overview.readiness.admin ? "Allowlist attiva" : "ADMIN_EMAILS mancante",
    ok: overview.readiness.admin,
  },
  {
    label: "HTTPS pubblico",
    detail: overview.readiness.httpsAppUrl ? "Configurato" : "APP_URL non HTTPS",
    ok: overview.readiness.httpsAppUrl,
  },
];

export function AdminPage({ config, onProfile }: Props) {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [credits, setCredits] = useState(5);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const canAdmin = Boolean(config.profile?.isAdmin);

  useEffect(() => {
    if (!canAdmin) return;
    let active = true;
    setLoading(true);
    loadAdminOverview()
      .then((result) => {
        if (active) setOverview(result);
      })
      .catch((reason) => {
        if (active) {
          setError(
            reason instanceof Error ? reason.message : "Dashboard non disponibile.",
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [canAdmin]);

  useEffect(() => {
    if (!canAdmin) return;
    let active = true;
    const timer = window.setTimeout(() => {
      setUsersLoading(true);
      loadAdminUsers(query)
        .then((result) => {
          if (!active) return;
          setUsers(result.users);
          setTotalUsers(result.total);
          setSelected((current) => {
            const visibleIds = new Set(result.users.map((user) => user.id));
            return new Set([...current].filter((id) => visibleIds.has(id)));
          });
        })
        .catch((reason) => {
          if (active) {
            setError(
              reason instanceof Error ? reason.message : "Utenti non disponibili.",
            );
          }
        })
        .finally(() => {
          if (active) setUsersLoading(false);
        });
    }, query ? 250 : 0);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [canAdmin, query]);

  const selectedUsers = useMemo(
    () => users.filter((user) => selected.has(user.id)),
    [selected, users],
  );

  const refresh = async () => {
    setError("");
    setLoading(true);
    setUsersLoading(true);
    try {
      const [nextOverview, nextUsers] = await Promise.all([
        loadAdminOverview(),
        loadAdminUsers(query),
      ]);
      setOverview(nextOverview);
      setUsers(nextUsers.users);
      setTotalUsers(nextUsers.total);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Aggiornamento non riuscito.");
    } finally {
      setLoading(false);
      setUsersLoading(false);
    }
  };

  const applyCredits = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selected.size) {
      setError("Seleziona almeno un utente.");
      return;
    }
    setError("");
    setNotice("");
    setSaving(true);
    try {
      const result = await grantAdminCredits({
        accountIds: [...selected],
        credits,
        reason,
      });
      setNotice(
        `${credits} VAFFA aggiunti a ${result.updated} ${
          result.updated === 1 ? "utente" : "utenti"
        }.`,
      );
      setSelected(new Set());
      setReason("");
      await refresh();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Accredito non riuscito.");
    } finally {
      setSaving(false);
    }
  };

  if (!config.profile) {
    return (
      <main className="admin-gate">
        <LockKeyhole aria-hidden="true" />
        <span>Area privata</span>
        <h1>Amministrazione Vaffangoogle</h1>
        <p>Accedi con un indirizzo autorizzato in ADMIN_EMAILS.</p>
        <button type="button" onClick={onProfile}>Accedi</button>
        <a href="/"><ChevronLeft /> Torna al sito</a>
      </main>
    );
  }

  if (!config.profile.isAdmin) {
    return (
      <main className="admin-gate admin-gate--denied">
        <AlertTriangle aria-hidden="true" />
        <span>Accesso negato</span>
        <h1>Questo profilo non è amministratore</h1>
        <p>L’area e le API sono riservate agli indirizzi presenti in ADMIN_EMAILS.</p>
        <button type="button" onClick={onProfile}>Cambia profilo</button>
        <a href="/"><ChevronLeft /> Torna al sito</a>
      </main>
    );
  }

  return (
    <main className="admin-page">
      <header className="admin-header">
        <div>
          <Brand compact />
          <span className="admin-kicker"><ShieldCheck /> Console privata</span>
        </div>
        <div className="admin-header__actions">
          <button type="button" onClick={refresh} disabled={loading}>
            <RefreshCw className={loading ? "spin" : ""} /> Aggiorna
          </button>
          <button type="button" onClick={onProfile}>{config.profile.email}</button>
          <a href="/">Sito</a>
        </div>
      </header>

      <section className="admin-title">
        <div>
          <span>Controllo operativo</span>
          <h1>Numeri chiari. Crediti sotto controllo.</h1>
          <p>Contenuti delle battute esclusi: qui restano solo dati operativi e movimenti.</p>
        </div>
        <div className="admin-live"><i /> Sistema online</div>
      </section>

      {error ? <div className="admin-alert admin-alert--error">{error}</div> : null}
      {notice ? <div className="admin-alert admin-alert--success"><Check /> {notice}</div> : null}

      {overview ? (
        <>
          <section className="admin-stats" aria-label="Indicatori principali">
            <article><UsersRound /><span>Utenti registrati</span><strong>{overview.metrics.registeredUsers}</strong><small>{overview.metrics.activeUsers30d} attivi in 30 gg</small></article>
            <article><Sparkles /><span>Generazioni 30 gg</span><strong>{overview.metrics.generations30d}</strong><small>senza salvare i testi</small></article>
            <article><WalletCards /><span>Crediti disponibili</span><strong>{overview.metrics.registeredCredits}</strong><small>sui profili registrati</small></article>
            <article><CircleDollarSign /><span>Incasso 30 gg</span><strong>{formatMoney(overview.metrics.grossRevenueCents30d)}</strong><small>{overview.metrics.paidOrders30d} ordini registrati</small></article>
          </section>
          <section className="admin-mini-stats" aria-label="Indicatori secondari">
            <span><b>{overview.metrics.anonymousWallets}</b> wallet anonimi</span>
            <span><b>{overview.metrics.purchasedCredits30d}</b> crediti acquistati · 30 gg</span>
            <span><b>{overview.metrics.adminGrantedCredits30d}</b> crediti admin · 30 gg</span>
            <span><b>{overview.readiness.mediaRetentionHours}h</b> conservazione MP3</span>
          </section>

          <section className="admin-readiness">
            <div className="admin-section-heading">
              <div><BarChart3 /><span><small>Go-live</small><h2>Stato produzione</h2></span></div>
              <b>{readinessItems(overview).filter((item) => item.ok).length}/5 pronti</b>
            </div>
            <div className="readiness-grid">
              {readinessItems(overview).map((item) => (
                <div className={item.ok ? "readiness-item readiness-item--ok" : "readiness-item readiness-item--missing"} key={item.label}>
                  {item.ok ? <Check /> : <AlertTriangle />}
                  <span><strong>{item.label}</strong><small>{item.detail}</small></span>
                </div>
              ))}
              <div className="readiness-item readiness-item--warning">
                <Database />
                <span><strong>Archivio dati</strong><small>JSON · una sola istanza</small></span>
              </div>
            </div>
          </section>
        </>
      ) : loading ? (
        <div className="admin-loading"><LoaderCircle className="spin" /> Carico i dati…</div>
      ) : null}

      <div className="admin-workspace">
        <section className="admin-users">
          <div className="admin-section-heading admin-section-heading--users">
            <div><UserRoundCheck /><span><small>Clienti</small><h2>Utenti e crediti</h2></span></div>
            <b>{totalUsers} risultati</b>
          </div>
          <label className="admin-search">
            <Search aria-hidden="true" />
            <span className="sr-only">Cerca per email o ID</span>
            <input
              type="search"
              placeholder="Cerca email o ID…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            {usersLoading ? <LoaderCircle className="spin" /> : null}
          </label>

          {users.length ? (
            <>
              <button
                className="admin-select-all"
                type="button"
                onClick={() =>
                  setSelected((current) =>
                    current.size === users.length
                      ? new Set()
                      : new Set(users.map((user) => user.id)),
                  )
                }
              >
                {selected.size === users.length ? "Deseleziona tutti" : "Seleziona tutti i risultati"}
              </button>
              <div className="admin-user-list">
                {users.map((user) => {
                  const checked = selected.has(user.id);
                  return (
                    <label className={`admin-user ${checked ? "admin-user--selected" : ""}`} key={user.id}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setSelected((current) => {
                            const next = new Set(current);
                            if (next.has(user.id)) next.delete(user.id);
                            else next.add(user.id);
                            return next;
                          })
                        }
                      />
                      <span className="admin-user__identity">
                        <strong>{user.email}</strong>
                        <small>Iscritto {formatDate(user.createdAt)}</small>
                      </span>
                      <span className="admin-user__data"><b>{user.credits}</b><small>VAFFA</small></span>
                      <span className="admin-user__data"><b>{user.generations}</b><small>creati · 30 gg</small></span>
                      <span className="admin-user__data admin-user__last"><b><Clock3 /> {formatDate(user.lastActivityAt)}</b><small>ultima attività</small></span>
                    </label>
                  );
                })}
              </div>
            </>
          ) : !usersLoading ? (
            <p className="admin-empty">Nessun utente corrisponde alla ricerca.</p>
          ) : null}
        </section>

        <aside className="admin-credit-panel">
          <div className="admin-section-heading">
            <div><Coins /><span><small>Operazione</small><h2>Aggiungi crediti</h2></span></div>
          </div>
          <div className="admin-selection-summary">
            <strong>{selected.size}</strong>
            <span>{selected.size === 1 ? "utente selezionato" : "utenti selezionati"}</span>
          </div>
          {selectedUsers.length ? (
            <div className="admin-selected-emails">
              {selectedUsers.slice(0, 4).map((user) => <span key={user.id}>{user.email}</span>)}
              {selectedUsers.length > 4 ? <span>+{selectedUsers.length - 4} altri</span> : null}
            </div>
          ) : null}
          <form onSubmit={applyCredits}>
            <label htmlFor="admin-credits">VAFFA da aggiungere a ciascuno</label>
            <input
              id="admin-credits"
              type="number"
              min="1"
              max="1000"
              value={credits}
              onChange={(event) => setCredits(Number(event.target.value))}
              required
            />
            <div className="admin-credit-presets">
              {[3, 5, 10, 25].map((value) => (
                <button type="button" key={value} onClick={() => setCredits(value)}>+{value}</button>
              ))}
            </div>
            <label htmlFor="admin-reason">Motivo interno</label>
            <textarea
              id="admin-reason"
              value={reason}
              minLength={3}
              maxLength={160}
              placeholder="Es. recupero pagamento o gesto assistenza"
              onChange={(event) => setReason(event.target.value)}
              required
            />
            <button className="admin-credit-submit" type="submit" disabled={saving || !selected.size}>
              {saving ? <LoaderCircle className="spin" /> : <Coins />}
              {saving ? "Registro…" : `Aggiungi ${credits || 0} VAFFA`}
            </button>
          </form>
          <p><ShieldCheck /> Ogni operazione viene registrata nell’audit log.</p>
        </aside>
      </div>

      {overview ? (
        <div className="admin-ledgers">
          <section className="admin-audit">
            <div className="admin-section-heading">
              <div><Database /><span><small>Tracciabilità</small><h2>Ultimi accrediti manuali</h2></span></div>
              <b>{overview.metrics.adminGrantedCredits30d} crediti · 30 gg</b>
            </div>
            {overview.recentAdjustments.length ? (
              <div className="admin-audit-list">
                {overview.recentAdjustments.map((item) => (
                  <article key={item.id}>
                    <span><strong>+{item.credits} VAFFA</strong><small>{formatDate(item.createdAt)}</small></span>
                    <span><b>{item.targetEmail}</b><small>{item.reason}</small></span>
                    <span><small>da</small><b>{item.adminEmail}</b></span>
                  </article>
                ))}
              </div>
            ) : (
              <p className="admin-empty">Nessun accredito manuale registrato.</p>
            )}
          </section>

          <section className="admin-audit admin-purchases">
            <div className="admin-section-heading">
              <div><CircleDollarSign /><span><small>Stripe</small><h2>Ultimi pagamenti</h2></span></div>
              <b>{overview.metrics.paidOrders30d} ordini · 30 gg</b>
            </div>
            {overview.recentPurchases.length ? (
              <div className="admin-purchase-list">
                {overview.recentPurchases.map((item) => (
                  <article key={item.id}>
                    <span><strong>{formatMoney(item.amountTotal ?? 0)}</strong><small>{formatDate(item.processedAt)}</small></span>
                    <span><b>{item.customerEmail ?? "Email non disponibile"}</b><small>{item.credits} VAFFA accreditati</small></span>
                    <code>{item.id}</code>
                  </article>
                ))}
              </div>
            ) : (
              <p className="admin-empty">Nessun pagamento registrato.</p>
            )}
          </section>
        </div>
      ) : null}
    </main>
  );
}
